import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function WriteReview() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "", display_name: "", permission_public: false });

  useEffect(() => {
    api.get(`/reviews/form/${token}`)
      .then((r) => { setInfo(r.data); setForm((f) => ({ ...f, display_name: r.data.display_name || "" })); })
      .catch((e) => setErr(apiErr(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) { toast.error("Please choose a star rating."); return; }
    if (!form.body.trim()) { toast.error("Please write a short review."); return; }
    setBusy(true);
    try { await api.post(`/reviews/submit/${token}`, form); setDone(true); }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center px-4 py-10">
      <div className="max-w-lg w-full bg-white rounded-3xl border border-[#E6DEC3] p-8 shadow-[0_4px_30px_rgba(44,38,33,0.06)]" data-testid="review-form-card">
        <img src="/masaya-logo.png" alt="Masaya" className="h-12 w-auto mx-auto" />
        {children}
      </div>
    </div>
  );

  if (err) return <Shell><p className="mt-6 text-center text-[#635B52]" data-testid="review-error">{err}</p><Link to="/" className="mt-6 block text-center text-[#B87333]">Back to home</Link></Shell>;
  if (!info) return <Shell><p className="mt-6 text-center text-[#635B52]">Loading…</p></Shell>;
  if (done || info.already_submitted) return (
    <Shell>
      <CheckCircle2 className="mx-auto mt-6 text-green-600" size={44} />
      <h1 className="mt-3 font-serif text-2xl text-[#2C2621] text-center">Thank you for your feedback!</h1>
      <p className="mt-2 text-center text-[#635B52]">{done ? "Your review has been received and will be reviewed before appearing publicly." : "A review has already been submitted for this booking."}</p>
      <Link to="/" className="mt-6 block text-center bg-[#B87333] text-white rounded-full py-3 font-medium" data-testid="review-home">Back to home</Link>
    </Shell>
  );

  return (
    <Shell>
      <h1 className="mt-5 font-serif text-2xl text-[#2C2621] text-center">How did we do?</h1>
      <p className="mt-1 text-center text-sm text-[#635B52]">{info.service_label} · {info.booking_number}</p>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <div className="flex justify-center gap-1" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`} aria-label={`${n} stars`}>
              <Star size={34} className={n <= form.rating ? "fill-[#B87333] text-[#B87333]" : "text-[#E6DEC3]"} />
            </button>
          ))}
        </div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" className="w-full rounded-xl border border-[#E6DEC3] px-4 py-3 text-sm" data-testid="review-title" />
        <textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell us about your experience*" className="w-full rounded-xl border border-[#E6DEC3] px-4 py-3 text-sm" data-testid="review-body" />
        <div>
          <label className="text-xs font-medium text-[#635B52]">Display name</label>
          <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 text-sm" data-testid="review-name" />
        </div>
        <label className="flex items-start gap-2 text-xs text-[#635B52] cursor-pointer">
          <input type="checkbox" checked={form.permission_public} onChange={(e) => setForm({ ...form, permission_public: e.target.checked })} className="mt-0.5 accent-[#B87333]" data-testid="review-permission" />
          I give Masaya Hospitality Cleaning Services permission to display my review publicly on its website and marketing materials.
        </label>
        <button type="submit" disabled={busy} className="w-full bg-[#B87333] text-white rounded-full py-3 font-semibold text-sm disabled:opacity-60" data-testid="review-submit">{busy ? "Submitting…" : "Submit review"}</button>
      </form>
    </Shell>
  );
}
